import { useCallback } from 'react';

import {
  useVerifyEmailStore,
  verifyEmailStoreSelector,
} from '@editor/stores/verifyEmailStore';
import analytics from '@repo/analytics';
import { Button, secondaryTheme } from '@repo/ui/components/Buttons';
import FacebookIcon from '@repo/ui/components/Icon/FacebookIcon';
import GoogleIcon from '@repo/ui/components/Icon/GoogleIcon';
import { Spacer } from '@repo/ui/components/Spacer';
import { H2, P2, P3 } from '@repo/ui/components/Typography';
import { Logo } from '@repo/ui/deprecated/Logo';
import { ProfileImage } from '@repo/ui/deprecated/ProfileImage';
import { getDeviceScreenSizeType } from '@repo/ui/queries';
import { themeStyle, useNewThemeActive } from '@repo/ui/theming';
import {
  buildProfileImageUrl,
  buildSignupUrl,
  buildWebsiteUrl,
  LICENSING_PAGE_URL,
} from '@repo/utils/url';
import Cookies from 'js-cookie';

import { USER_API_URL } from '../../../api/constants';
import {
  SOCIAL_FACEBOOK,
  SOCIAL_GOOGLE,
} from '../../../global/analytics/events';
import { IMPACT_CLICK_ID } from '../../../global/constants';
import { getAdClickId } from '../../../utils/adClickId';
import { readUtmData } from '../../../utils/utm';
import Form from '../Form/Form';
import { AuthComponentProps } from '../types';

import {
  Content,
  Divider,
  OrWrapper,
  OtherProviders,
  PolicyLabel,
  SwitchModeLabel,
} from './styles';
import { SocialQueryParameterProps } from './types';

function buildRedirectUrl(redirectUrl?: string): string | undefined {
  if (!redirectUrl || typeof window === 'undefined') {
    return undefined;
  }

  const url = new URL(redirectUrl, window?.location.origin);

  return encodeURIComponent(url.toString());
}

/** @knipignore - used in website  */
export const getSocialQueryParameter = (
  props: SocialQueryParameterProps,
  rerequest = false
): string => {
  const params = [];
  if (props.inviteCode) {
    params.push(`invite=${props.inviteCode}`);
  } else if (props.referralCode) {
    params.push(`referral=${props.referralCode}`);
  } else if (props.promoCode) {
    params.push(`promoCode=${props.promoCode}`);
  }
  if (props.impactClickId) {
    params.push(`impactClickId=${props.impactClickId}`);
  }
  if (props.adClickId) {
    params.push(`adClickId=${props.adClickId}`);
  }
  if (props.utm) {
    params.push(`utm=${encodeURIComponent(JSON.stringify(props.utm))}`);
  }
  if (props.signupUrl) {
    params.push(`signupUrl=${props.signupUrl}`);
  }
  if (props.deviceScreenSizeType) {
    params.push(`deviceScreenSizeType=${props.deviceScreenSizeType}`);
  }

  if (props.redirectUrlAfterAuth) {
    params.push(
      `redirectUrlAfterAuth=${buildRedirectUrl(props.redirectUrlAfterAuth)}`
    );
  } else if (props.signupUrl) {
    params.push(`redirectUrlAfterAuth=${buildRedirectUrl(props.signupUrl)}`);
  }

  if (rerequest) {
    params.push('rerequest=true');
  }

  if (params.length) {
    return `?${params.join('&')}`;
  }
  return '';
};

export const onGoogleLogin = (customProps?: AuthComponentProps): void => {
  if (window?.location) {
    const _props = { ...customProps } as SocialQueryParameterProps;

    const impactClickId = Cookies.get(IMPACT_CLICK_ID);
    if (impactClickId) {
      _props.impactClickId = impactClickId;
    }

    const adClickId = getAdClickId();
    if (adClickId) {
      _props.adClickId = adClickId;
    }

    const utm = readUtmData();
    if (utm) {
      _props.utm = utm;
    }

    const signupUrl = buildSignupUrl();
    if (signupUrl) {
      _props.signupUrl = signupUrl;
    }

    const deviceScreenSizeType = getDeviceScreenSizeType();
    if (deviceScreenSizeType) {
      _props.deviceScreenSizeType = deviceScreenSizeType;
    }

    let redirectURL = `${USER_API_URL}/sessions/google/create`;
    const params = getSocialQueryParameter(_props, false);
    if (params) {
      redirectURL += params;
    }
    analytics.track(SOCIAL_GOOGLE);
    window.location.href = redirectURL;
  }
};

/**
 * The Authentication is the left column of the AuthComponent on sign-up
 * and the modal content on sign in
 */
const Authentication = (props: AuthComponentProps): JSX.Element => {
  const isNewThemeActive = useNewThemeActive();

  const onFacebookLogin = (): void => {
    if (window?.location) {
      const _props = { ...props } as SocialQueryParameterProps;

      const impactClickId = Cookies.get(IMPACT_CLICK_ID);
      if (impactClickId) {
        _props.impactClickId = impactClickId;
      }

      const adClickId = getAdClickId();
      if (adClickId) {
        _props.adClickId = adClickId;
      }

      const utm = readUtmData();
      if (utm) {
        _props.utm = utm;
      }

      const signupUrl = buildSignupUrl();
      if (signupUrl) {
        _props.signupUrl = signupUrl;
      }

      const deviceScreenSizeType = getDeviceScreenSizeType();
      if (deviceScreenSizeType) {
        _props.deviceScreenSizeType = deviceScreenSizeType;
      }

      let redirectURL = `${USER_API_URL}/sessions/facebook/create`;
      const params = getSocialQueryParameter(_props, _props.rerequest);
      if (params) {
        redirectURL += params;
      }
      analytics.track(
        SOCIAL_FACEBOOK,
        props.rerequest ? { label: 'rerequest: true;' } : undefined
      );
      window.location.href = redirectURL;
    }
  };

  const { redirectUrlAfterAuth, onSubmit } = props;

  const setRedirectUrl = useVerifyEmailStore(
    verifyEmailStoreSelector.setRedirectUrl
  );

  const handleSubmit = useCallback(() => {
    if (redirectUrlAfterAuth) {
      setRedirectUrl(redirectUrlAfterAuth);
    }
    onSubmit?.();
  }, [onSubmit, redirectUrlAfterAuth, setRedirectUrl]);

  const socialButtonProps = {
    width: 'unset',
    height: '40px',
    theme: secondaryTheme,
  };

  const invitedBy = props.invitedBy
    ? {
        ...props.invitedBy,
        name: props.invitedBy.name || props.invitedBy.slug,
        profileImage: buildProfileImageUrl(props.invitedBy.profileImage),
      }
    : null;

  const title =
    props.title ??
    (props.isSignup ? 'Create Free Account' : 'Welcome To Kittl');

  const handleGoogleLogin = useCallback(() => {
    onGoogleLogin(props);
  }, [props]);

  return (
    <Content data-testid="auth-modal--container">
      <Logo height={40} style={{ marginBottom: '24px' }} />
      {!invitedBy && (
        <>
          <H2>{title}</H2>
          {!props.isSignup && (
            <P2 style={{ marginTop: '8px' }}>Sign in below</P2>
          )}
          <Spacer height={props.isSignup ? '48px' : '24px'} />
        </>
      )}
      {!!(props.isSignup && invitedBy) && (
        <>
          <ProfileImage profile={invitedBy} size="60px" />
          <Spacer height="12px" />
          <P2>
            {invitedBy.name} has invited you to join Kittl. Sign up now to claim
            your reward!
          </P2>
          <Spacer height="32px" />
        </>
      )}
      {props.customContent && (
        <>
          {props.customContent}
          <Spacer height="24px" />
        </>
      )}
      <OtherProviders>
        <Button
          name="googleSignInButton"
          {...socialButtonProps}
          dataTestId="google-sign-in"
          icon={{ component: GoogleIcon, height: '16px' }}
          label="Google"
          onClick={handleGoogleLogin}
        />
        <Spacer width="12px" />
        <Button
          name="facebookLoginButton"
          {...socialButtonProps}
          dataTestId="facebook-sign-in"
          icon={{ component: FacebookIcon, height: '16px' }}
          label="Facebook"
          onClick={onFacebookLogin}
        />
      </OtherProviders>

      <OrWrapper>
        <Divider />
        <P3 color={themeStyle.varInkMedium}>OR</P3>
        <Divider />
      </OrWrapper>

      <Form
        disableOnError
        callToAction={props.callToAction}
        email={props.email}
        error={props.error}
        inviteCode={props.inviteCode}
        isSignup={props.isSignup}
        pow={props.pow}
        promoCode={props.promoCode}
        referralCode={props.referralCode}
        teamInviteCode={props.teamInviteCode}
        onSubmit={handleSubmit}
      />

      {!props.isSignup && (
        <>
          <Spacer height="8px" />
          <SwitchModeLabel
            noDecoration
            as="label"
            data-testid="forgot-password"
            isNewThemeActive={isNewThemeActive}
            onClick={() => props.onForgotPassword?.()}
          >
            Forgot your password?
          </SwitchModeLabel>
          {props.onGoToSignup && (
            <>
              <Spacer height="24px" />
              <P2>Don't have an account yet?</P2>
              <SwitchModeLabel
                as="label"
                isNewThemeActive={isNewThemeActive}
                onClick={props.onGoToSignup}
              >
                Sign up
              </SwitchModeLabel>
            </>
          )}
        </>
      )}
      {props.isSignup && (
        <>
          <PolicyLabel>
            By clicking on “{props.callToAction ?? 'Create Account'}”, you agree
            to our{' '}
            <a
              href={buildWebsiteUrl('/terms')}
              rel="noreferrer"
              target="_blank"
            >
              Terms of Use
            </a>{' '}
            and{' '}
            <a href={LICENSING_PAGE_URL} rel="noreferrer" target="_blank">
              License Terms
            </a>
            . In our{' '}
            <a
              href={buildWebsiteUrl('/privacy')}
              rel="noreferrer"
              target="_blank"
            >
              Privacy Policy
            </a>
            , we explain how we process your personal data and what rights you
            have.
          </PolicyLabel>
          {props.onGoToLogin && (
            <>
              <Spacer height="24px" />
              <P2>Already have an account?</P2>
              <SwitchModeLabel
                as="label"
                isNewThemeActive={isNewThemeActive}
                onClick={props.onGoToLogin}
              >
                Sign in
              </SwitchModeLabel>
            </>
          )}
          {!props.onGoToLogin && !props.invitedBy && !props.customFooter && (
            <Spacer height="48px" />
          )}
          {props.customFooter}
        </>
      )}
    </Content>
  );
};

export default Authentication;
